import {ComponentProvider} from '@smart/react-components';
import * as React from 'react';
import {useEffect, useState} from 'react';
import type {AppModel} from '../../bff/app-model/create-content-model';
import type {ViewTypes} from '../common/types';
import {useAppModel} from '../hooks/use-app-model';
import {useEntry} from '../hooks/use-entry';
import {useFeatureAppEnvHook} from '../hooks/use-feature-app-env';
import {tokenKey, useToken} from '../hooks/use-token';
import {useViews} from '../hooks/use-views';
import type {JSONValue} from './easy-form/types';

export interface AppProps {
  readonly done?: () => void;
}

interface SessionState {
  view: ViewTypes;
  state: JSONValue;
}

const sessionStateKey = `historyState-`;

export const AppModelContext = React.createContext<AppModel>({} as AppModel);

export function App({done}: AppProps): JSX.Element | null {
  const {config, featureServices} = useFeatureAppEnvHook();
  const historyService = featureServices[`s2:history`];
  const {sessionToken, URLToken, setToken} = useToken();
  const appModel = useAppModel(done);

  const {getViewByName, views, defaultView, restartJourney} = useViews();

  const [currentView, setCurrentView] = useState<{
    view: ViewTypes;
    props: any | undefined;
  }>({
    view: defaultView,
    props: undefined,
  });
  const [entry, setEntry] = useEntry();

  const route = React.useMemo(() => {
    const {view, props} = currentView;
    const ViewComp = getViewByName(view).component;
    return <ViewComp {...props} />;
  }, [currentView.view, currentView.props]);

  const getSessionState = (view: ViewTypes) => {
    const sessionState = (JSON.parse(
      sessionStorage.getItem(`${sessionStateKey}${view}`) || `null`,
    ) || undefined) as SessionState | undefined;

    return sessionState && sessionState.view === view
      ? sessionState.state
      : undefined;
  };

  const setSessionState = (view: ViewTypes, state: unknown) => {
    sessionStorage.setItem(
      `${sessionStateKey}${view}`,
      JSON.stringify({
        view,
        state,
      } as SessionState),
    );
  };

  useEffect(() => {
    if (!config) throw new Error(`Missing FeatureAppConfig`);

    // history listener, We will change history soon
    historyService.rootHistory.listen((newLocation) => {
      const searchParams = new URLSearchParams(newLocation.location.search);
      const targetView = (searchParams.get(`view`) || defaultView) as ViewTypes;

      // stores view state in session when present
      if (newLocation.location.state)
        setSessionState(targetView, newLocation.location.state);
      setCurrentView({
        view: targetView,
        props: newLocation.location.state,
      });
    });
    const location = historyService.rootHistory.location;
    const initialParams = new URLSearchParams(location.search);
    const view = (initialParams.get(`view`) || defaultView) as ViewTypes;
    const viewConfig = getViewByName(view);
    // if view does not exist, restart
    if (!views.includes(view)) {
      console.warn(`Journey Restarted - View nonexistent `);
      restartJourney();
      return;
    }

    // restarts journey if token present and view doest not support it
    if (URLToken && viewConfig.tokenRequired !== `required`) {
      console.warn(`Journey Restarted - Token NOT required on view '${view}'`);
      restartJourney();
      return;
    }

    // restart journey if no token is present for a required view
    // if URL is not present, replace token with stored token
    const token = URLToken || sessionToken;
    if (!token && viewConfig.tokenRequired === `required`) {
      console.warn(`Journey Restarted - Token required on view '${view}'`);
      restartJourney();
      return;
    }

    if (token) {
      if (viewConfig.tokenRequired === `required`) {
        // stores token if present and page requires it
        setToken(token);
      } else if (viewConfig.tokenRequired === `new`) {
        setToken(null);
        // deletes token if page is a empty entrypoint
      }
    }

    // saves entrypoint if non existent for future check (case fo reloading)
    // or if the URL has a token, meaning user is coming form an email and thus restarting the journey
    // or if page is a empty entrypoint
    if (!entry || URLToken || viewConfig.tokenRequired === `new`) {
      setEntry(view);
    }

    // check if there is 'token' param
    if (initialParams.has(tokenKey)) {
      // removes 'token' parameter
      initialParams.delete(tokenKey);
      // just change history to load correct component
      historyService.rootHistory.push({
        ...location,
        search: initialParams.toString(),
      });
      return;
    }
    // or set proper view
    setCurrentView({
      view,
      props: getSessionState(view),
    });
  }, []);

  return appModel ? (
    <AppModelContext.Provider value={appModel}>
      <ComponentProvider theme="light">{route}</ComponentProvider>
    </AppModelContext.Provider>
  ) : null;
}
